import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TaskGridRowData, SeverityStatusDataWithDate } from 'src/@types/crs/case';
import SeverityStatus, { AlertSeverity } from 'src/components/SeverityStatus';
import { FhirResource, Reference, Task } from 'src/careflow/ph-core/fhir-client/resources';
import PeriodFilter, { onDateChange } from 'src/components/PeriodFilter';
import CheckboxList from 'src/components/CheckboxList';
import useObjectState from 'src/hooks/useObjectState';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { TaskOpenFrom, TaskStatus, getOwnerDisplayLabel } from './TaskModal';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import TaskActionMenu, { OnInternalUrlClick, UrlData } from './TaskActionMenu';
import SearchMemberModal from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMemberModal';
import { useSnackbar } from 'notistack';
import { updateTask } from '../../main/task.api';
import { onSelectResource } from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import { momentDateComparator } from 'src/utils/dates';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import Program from 'src/careflow/common/Program';
import TaskDetailDrawer from '../../common/TaskDetailDrawer';
import './styles.css';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { WrappedHealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import ModalCommunication from 'src/careflow/common/ModalCommunication';
import useTaskContext from '../../common/hook/useTaskContext';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useClientGrid from 'src/careflow/ph-core/grid/hook/useClientGrid';
import CellRow from 'src/careflow/common/CellRow';
import CustomLink from 'src/careflow/common/CustomLink';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import { TaskPermissions } from 'src/careflow/helpers/types';
import IframeModal from 'src/careflow/common/IframeModal';
import CollapsibleDataGrid from 'src/careflow/ph-core/grid/react/CollapsibleDataGrid';
import CaseTaskGrid, {
  SeverityStatusCount,
  filterTaskGridRows,
  getGoalsByPathWay,
  getStatusCountByPathway,
} from 'src/careflow/care-coordination/case/detail/components/CaseTaskGrid';
import { useEffect, useMemo, useState } from 'react';
import { TaskByPathway } from '../TasksByPathway/TasksByPathway';
import {
  getTaskGridRows,
  shouldIncludeTaskByChecklistFilters,
  getReferenceResourceId,
} from 'src/careflow/helpers/common';
import AddReferral from 'src/careflow/care-coordination/inbound-referral/AddReferral';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { TASK_TYPES } from 'src/careflow/helpers/constants';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { AppointmentForm } from 'src/careflow/care-coordination/patient/appointments/AppointmentForm';
import { commonMenuTaskHandlers } from '../../common/handlers';
import TaskContextProvider from '../../common/provider';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { CREATE_TASK, UPDATE_TASK, useEventBus } from 'src/event-bus';
import useGridFilterConsolidation from 'src/careflow/ph-core/grid/hook/useGridFilterConsolidation';
import { useTaskActivityDefinition } from 'src/careflow/helpers/hooks';
import { TaskModalResolver } from './TaskModalResolver';
import { GridFilterDrawerProps } from 'src/careflow/ph-core/grid/react/DataGridWithFilters/DataGridWithFilters';
import { GridColDef } from '@mui/x-data-grid';

export interface TaskFilters {
  selectedOwners: Reference[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  status: TaskStatus[];
  linked: boolean;
}
interface TasksGridProps extends GridFilterDrawerProps<TaskFilters> {
  tasks: TaskGridRowData[];
  owners: Reference[];
  isNestedGrid?: boolean;
  showPagination?: boolean;
  patient: WrappedPatient | null;
  carePlan?: WrappedCarePlan | null;
  goal?: WrappedGoal | null;
  healthCareServices?: WrappedHealthcareService[] | null;
  title?: string;
  buttonTitle?: string;
  taskBasedOn?: WrappedTask | null;
  onAssessmentSelect?: (task: Task) => void;
  pathwaysList?: TaskByPathway[];
  selectedPathway: string;
  handlePathwayTabChange: (event: React.SyntheticEvent, value: any) => void;
  includeSubTasks?: boolean;
  serviceRequests?: WrappedServiceRequest[];
  openFrom?: TaskOpenFrom;
  goalTaskSubTask?: boolean;
  conditions?: WrappedCondition[];
  observations?: WrappedObservation[];
  carePlanTaskIds?: string[];
  allTasksEnabled?: boolean;
  childrenButtons?: React.ReactNode;
}

export const initialTaskFilters: TaskFilters = {
  selectedOwners: [],
  startDate: null,
  endDate: null,
  status: [],
  linked: false,
};

const TasksGrid = ({
  tasks,
  owners,
  isNestedGrid,
  showPagination,
  onSearchTextFieldChange,
  searchTextFieldValue,
  onFilterDrawerOpen,
  onFilterDrawerClose,
  isFilterDrawerOpen,
  onApplyFilters,
  filterValues,
  patient,
  carePlan,
  goal,
  healthCareServices,
  title,
  buttonTitle,
  taskBasedOn,
  onAssessmentSelect,
  pathwaysList,
  selectedPathway,
  handlePathwayTabChange,
  includeSubTasks = true,
  serviceRequests,
  openFrom,
  goalTaskSubTask,
  conditions,
  observations,
  carePlanTaskIds,
  allTasksEnabled,
  childrenButtons,
}: TasksGridProps) => {
  const [taskGridRows, setTasksGridRows] = useState<TaskGridRowData[]>([]);
  const { filters, updateFilters, onClearAllFilters } = useGridFilterConsolidation<TaskFilters>(
    filterValues,
    initialTaskFilters
  );

  const { i18n } = useLocales();
  const { configurations, componentsData } = useTenantConfigData();
  const { dispatchEvent } = useEventBus();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};
  const { planDefinition, statusCountByPathway } = componentsData?.task ?? {};

  const { sortModel, handleSortModelChange } = useClientGrid({
    defaultSort: [{ field: 'endDate', sort: 'asc' }],
  });

  const [
    {
      isTaskModalOpen,
      taskToEdit,
      taskToView,
      subTaskBasedOn,
      isTaskDetailDrawerOpen,
      isSearchMemberModalOpen,
      isAssignProgramModalOpen,
      addNoteToTask,
      isIframeModalOpen,
      urlData,
      isServiceRequestModalOpen,
      taskOutputServiceRequest,
      isAppointmentModalOpen,
      taskOuputAppointmentId,
      taskType,
    },
    updateState,
  ] = useObjectState<{
    isTaskModalOpen: boolean;
    taskToEdit: WrappedTask | null;
    taskToView: WrappedTask | null;
    subTaskBasedOn: WrappedTask | null;
    isSearchMemberModalOpen: boolean;
    isAssignProgramModalOpen: boolean;
    isTaskDetailDrawerOpen: boolean;
    addNoteToTask: boolean;
    isIframeModalOpen: boolean;
    urlData: UrlData | null;
    isServiceRequestModalOpen: boolean;
    taskOutputServiceRequest: WrappedServiceRequest | null;
    isAppointmentModalOpen: boolean;
    taskOuputAppointmentId: string | null;
    taskType: string | null;
  }>({
    taskToEdit: null,
    taskToView: null,
    subTaskBasedOn: null,
    isTaskModalOpen: false,
    isSearchMemberModalOpen: false,
    isAssignProgramModalOpen: false,
    isTaskDetailDrawerOpen: false,
    addNoteToTask: false,
    isIframeModalOpen: false,
    urlData: null,
    isServiceRequestModalOpen: false,
    taskOutputServiceRequest: null,
    isAppointmentModalOpen: false,
    taskOuputAppointmentId: null,
    taskType: null,
  });

  const {
    taskActivityDefinitionContext,
    rowDblClickHandlers,
    basedOn,
    filterTasksFromChecklistItem,
    isWorkflowContext,
  } = useTaskContext();

  const {
    notes,
    isAllowedToEdit,
    isAllowedToAssignProgram,
    isAllowedReferralInternal,
    isAllowedReferralExternal,
    isAllowedAppointmentScheduling,
    isAllowedWarmHandoff,
  } = usePermissionsContext<TaskPermissions>() ?? {};

  const {
    valueSets: [taskStatuses],
  } = useValueSetsByIdentifiers(['ph-task-status']);

  const taskTypeMappings = componentsData?.task?.taskTypeMappings || {};

  const getTaskTypeConfig = (taskCode: string) => taskTypeMappings[taskCode] || null;

  const { enqueueSnackbar } = useSnackbar();

  const handleInternalUrlClick: OnInternalUrlClick = (urlData) => {
    updateState({ urlData, isIframeModalOpen: true });
  };

  const buildTaskHandlerMap = useMemo(() => {
    const legacyPermissionMap = {
      isAllowedReferralInternal,
      isAllowedReferralExternal,
      isAllowedAppointmentScheduling,
      isAllowedWarmHandoff,
      isAllowedToAssignProgram,
    };

    const createActionHandlers = (
      taskServiceRequest?: WrappedServiceRequest,
      taskAppointment?: string
    ) => ({
      assessment: (task: WrappedTask) => onAssessmentSelect?.(task),
      referral: (task: WrappedTask, originalTaskType: string) => {
        console.log('Referral handler called for task:', task?.id);
        updateState({
          taskToEdit: task,
          isServiceRequestModalOpen: true,
          taskOutputServiceRequest: taskServiceRequest,
          taskType: originalTaskType, // Keep original task type for planDefinition mapping
        });
      },
      appointment_scheduling: (task: WrappedTask) =>
        updateState({
          taskToEdit: task,
          isAppointmentModalOpen: true,
          taskOuputAppointmentId: taskAppointment,
        }),
      program: (task: WrappedTask) =>
        updateState({
          taskToEdit: task,
          isAssignProgramModalOpen: true,
        }),
    });

    const buildHandlerForRow = (
      taskServiceRequest?: WrappedServiceRequest,
      taskAppointment?: string
    ) => {
      const actionHandlers = createActionHandlers(taskServiceRequest, taskAppointment);
      const handlerMap: any[] = [];

      Object.values(TASK_TYPES).forEach((taskType) => {
        const config = getTaskTypeConfig(taskType);
        if (config && config.enabled) {
          let permission: boolean | undefined = undefined;

          if (config.permission) {
            if (config.permission.startsWith('root.')) {
              permission = checkAclValidation({ acls: [config.permission] });
            } else {
              permission =
                legacyPermissionMap[config.permission as keyof typeof legacyPermissionMap];
            }
          }

          const handler = actionHandlers[config.actionType as keyof typeof actionHandlers];

          if (typeof handler === 'function') {
            handlerMap.push({
              taskType,
              permission,
              handler:
                config.actionType === 'referral'
                  ? (task: WrappedTask) => handler(task, taskType)
                  : handler,
            });
          }
        }
      });

      return handlerMap;
    };

    return buildHandlerForRow;
  }, [
    getTaskTypeConfig,
    isAllowedReferralInternal,
    isAllowedReferralExternal,
    isAllowedAppointmentScheduling,
    isAllowedWarmHandoff,
    isAllowedToAssignProgram,
    onAssessmentSelect,
    updateState,
  ]);

  const handleDateFilterChange =
    (dateProp: 'endDate' | 'startDate'): onDateChange =>
    (newValue) => {
      updateFilters({ [dateProp]: newValue });
    };

  const handleOwnerChange: onSelectResource = async (resource) => {
    updateState({ isSearchMemberModalOpen: false });
    const response = await updateTask(
      {
        ownerId: `${resource?.resourceType}/${resource?.id}`,
        ownerDisplay: getOwnerDisplayLabel(resource),
      },
      taskToEdit?.id as string
    );
    if (!response) {
      enqueueSnackbar('Unable to update Task Owner. Please Try Again', {
        variant: 'error',
      });
      return;
    }
    enqueueSnackbar('Task Owner Succesfully Updated');
    dispatchEvent(UPDATE_TASK);
  };

  const handleOnTaskEdit = (task: WrappedTask) => {
    updateState({ taskToEdit: task, isTaskModalOpen: true });
  };

  const handleOnTaskCopy = (task: WrappedTask) => {
    const { id, ...rest } = task;
    const taskCopy = { ...rest };
    updateState({ taskToEdit: taskCopy, isTaskModalOpen: true });
  };

  const handleAddNoteToTask = (task: WrappedTask) => {
    updateState({ taskToEdit: task, addNoteToTask: true });
  };

  const handleTaskToProgram = async (task: WrappedTask) => {
    updateState({ isAssignProgramModalOpen: false });
    const response = await updateTask({ basedOn: task }, taskToEdit?.id as string);
    if (!response) {
      enqueueSnackbar('Unable to update task. Please Try Again', {
        variant: 'error',
      });
      return;
    }
    enqueueSnackbar('Task Succesfully Updated');
    dispatchEvent(UPDATE_TASK);
  };

  const handleOpenTask = (task: WrappedTask | null) => {
    if (!task) return;
    const { handler } =
      rowDblClickHandlers?.find?.(({ taskType }) => taskType === task.getTaskType()) ?? {};
    if (handler) {
      handler(task);
      return;
    }
    updateState({ isTaskDetailDrawerOpen: true, taskToView: task });
  };

  const handleUpdateTaskFromResource = async (resource: FhirResource | null) => {
    if (!resource && !taskToEdit) return;

    const taskToEditOutput = taskToEdit?.output ?? [];

    const taskOutputValueReferenceServiceRequest = taskToEditOutput?.find(
      (output) => output?.valueReference?.reference === `${resource?.resourceType}/${resource?.id}`
    );
    if (taskOutputValueReferenceServiceRequest) return;

    const output = [
      ...taskToEditOutput,
      {
        type: {
          coding: [
            {
              code: `${resource?.resourceType}`,
              display: `${resource?.resourceType}`,
              system: `${configurations?.fhir?.baseUrl}/CodeSystem/ph-output-type`,
            },
          ],
        },
        valueReference: {
          reference: `${resource?.resourceType}/${resource?.id}`,
          type: `${resource?.resourceType}`,
          ...(resource?.resourceType === 'ServiceRequest'
            ? {
                display: (resource as any)?.identifier?.find?.(
                  (id: any) =>
                    id?.system ===
                    configurations?.fhir?.serviceRequestInternalNumberIdenitifierSystem
                )?.value,
              }
            : {}),
        },
      },
    ];
    const shouldAutoComplete =
      componentsData?.referral?.autoCloseTaskOnReferralCreation === true &&
      (taskType === TASK_TYPES.REFERRAL_INTERNAL ||
        taskType === TASK_TYPES.REFERRAL_EXTERNAL ||
        taskType === TASK_TYPES.WARM_HANDOFF) &&
      resource?.resourceType === 'ServiceRequest';

    const payload: any = { output };
    if (shouldAutoComplete) {
      payload.status = 'completed';
    }

    const response = await updateTask(payload, taskToEdit?.id as string);

    updateState({
      taskToEdit: null,
      isServiceRequestModalOpen: false,
      taskOutputServiceRequest: null,
    });

    if (!response) {
      enqueueSnackbar('Unable to update task. Please Try Again', {
        variant: 'error',
      });
      return;
    }
    enqueueSnackbar('Task Succesfully Updated');
    dispatchEvent(UPDATE_TASK); //aqui va a actualizar el serviceRequest
  };

  const columns: GridColDef[] = useMemo(
    () => {
      // Capture carePlanTaskIds in closure to ensure it's accessible in renderCell
      const capturedCarePlanTaskIds = carePlanTaskIds;
      
      return [
        {
          field: 'task',
          headerName: `${i18n('patients.details.tasks.task', 'crs')}`,
          flex: 2,
          renderCell: (params) => {
            const { task, wrappedTask } = params.row as TaskGridRowData;
            const getTaskNumber = wrappedTask
              ? wrappedTask.getInternalNumber(configurations?.fhir?.taskInternalNumberIdentifierSystem)
                ?? wrappedTask.getTaskNumberNAT(configurations?.fhir?.taskNATIdenitifierType)
                ?? ""
              : "";
            const associatedGoal: WrappedGoal | undefined =
              wrappedTask?.id ? (window as any).goalTaskAssociations?.[wrappedTask.id] : undefined;
            const goalLabel =
              associatedGoal?.getDescriptionDisplay?.() ??
              associatedGoal?.getPlanDefinitionDescription?.() ??
              '';
            return (
              <Box>
                <CustomLink
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenTask(wrappedTask);
                  }}
                >
                  <CellRow
                    tooltipTitle={i18n('tasks.taskNo', 'crs')}
                    title={getTaskNumber}
                  />
                </CustomLink>

                <CellRow tooltipTitle={task} title={task} maxLines={1} />

                {!!goalLabel && (
                  <Tooltip title={`Goal: ${goalLabel}`}>
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        Goal:
                      </Box>{' '}
                      {goalLabel}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        ...(!isNestedGrid && includeSubTasks ? [{
          field: 'subtasks',
          headerName: 'Subtasks',
          flex: 0.6,
          sortable: false,
          renderCell: (params) => {
            const { subTasks } = params.row as TaskGridRowData;
            const count = subTasks?.length ?? 0;
            if (count === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
            return (
              <Tooltip title="Subtasks can be viewed by expanding the row.">
                <Typography variant="body2" sx={{ cursor: 'help' }}>
                  {count}
                </Typography>
              </Tooltip>
            );
          },
        }] : []),
        {
          field: 'status',
          headerName: `${i18n('patients.details.tasks.status', 'crs')}`,
          flex: 0.8,
          renderCell: (params) => {
            const { status } = params.row as TaskGridRowData;
            return <CellRow shouldTruncateText={false} title={status} />;
          },
        },
        {
          field: 'owner',
          headerName: `${i18n('patients.details.tasks.owner', 'crs')}`,
          flex: 1,
          renderCell: (params) => {
            const { owner } = params.row as TaskGridRowData;
            return <CellRow shouldTruncateText={false} title={owner} />;
          },
        },
        {
          field: 'code',
          headerName: `${i18n('patients.details.tasks.code', 'crs')}`,
          flex: 0.7,
          renderCell: (params) => {
            const { codeDisplay } = params.row as TaskGridRowData;
            return <CellRow shouldTruncateText={false} title={codeDisplay} />;
          },
        },
        {
          field: 'startDate',
          headerName: `${i18n('patients.details.tasks.startDate', 'crs')}`,
          flex: 1,
          minWidth: 170,
          sortComparator: (_, __, cellParams1, cellParams2) => {
            const { date: date1 } = (cellParams1?.value as SeverityStatusDataWithDate) ?? {};
            const { date: date2 } = (cellParams2?.value as SeverityStatusDataWithDate) ?? {};
            const isAsc = sortModel?.[0]?.field === 'startDate' && sortModel?.[0]?.sort === 'asc';
    
            if (!date1.isValid() && !date2.isValid()) return 0;
            if (!date1.isValid()) return isAsc ? -1 : 1;
            if (!date2.isValid()) return isAsc ? 1 : -1;
    
            return momentDateComparator(date1, date2);
          },
          renderCell: (params) => {
            const { startDate } = params.row as TaskGridRowData;
            return <SeverityStatus status={{ ...startDate, severity: undefined }} />;
          },
        },
        {
          field: 'endDate',
          headerName: `${i18n('patients.details.tasks.endDate', 'crs')}`,
          flex: 1,
          minWidth: 190,
          sortComparator: (_, __, cellParams1, cellParams2) => {
            const { date: date1 } = (cellParams1?.value as SeverityStatusDataWithDate) ?? {};
            const { date: date2 } = (cellParams2?.value as SeverityStatusDataWithDate) ?? {};
            const isAsc = sortModel?.[0]?.field === 'endDate' && sortModel?.[0]?.sort === 'asc';
    
            if (!date1.isValid() && !date2.isValid()) return 0;
            if (!date1.isValid()) return isAsc ? -1 : 1;
            if (!date2.isValid()) return isAsc ? 1 : -1;
    
            return momentDateComparator(date1, date2);
          },
          renderCell: (params) => {
            const { endDate } = params.row as TaskGridRowData;
            return <SeverityStatus status={endDate} />;
          },
        },
        ...(allTasksEnabled ? [{
          field: 'linked',
          headerName: 'Linked',
          flex: 0.6,
          renderCell: (params) => {
            const { wrappedTask } = params.row as TaskGridRowData;
            const linkedReferences = wrappedTask?.basedOn || [];
            const linkedCount = linkedReferences.length;
            
            const taskId = wrappedTask?.id;
            const isLinkedToCarePlan = taskId && carePlanTaskIds?.includes(taskId);
            
            if (isLinkedToCarePlan) {
              return (
                <Box
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    display: 'inline-block',
                    textAlign: 'center'
                  }}
                >
                  Linked
                </Box>
              );
            }
            
            return null;
          },
        }] : []),
        {
          field: 'edit',
          headerName: '',
          flex: 0.5,
          sortable: false,
          renderCell: (params) => {
            const { wrappedTask, serviceRequest, appointment } = params.row as TaskGridRowData;
            return (
              <TaskActionMenu
                taskStatuses={taskStatuses?.asList?.() ?? []}
                taskExternal={wrappedTask}
                onEditTask={handleOnTaskEdit}
                onSearchMemberOpen={(task) => {
                  updateState({ taskToEdit: task, isSearchMemberModalOpen: true });
                }}
                onTaskOpen={handleOpenTask}
                onSuccessfulEdit={() => dispatchEvent(UPDATE_TASK, {isWorkflowContext})}
                onCopyTask={handleOnTaskCopy}
                onAddNoteToTask={handleAddNoteToTask}
                onInternalUrlClick={handleInternalUrlClick}
                isNestedGrid={goalTaskSubTask !== undefined ? goalTaskSubTask : isNestedGrid}
                onSubTask={(task) => updateState({ subTaskBasedOn: task,  isTaskModalOpen: true })}
                taskHandlerMap={buildTaskHandlerMap(serviceRequest, appointment)}
              />
            ); //TODO need to add functionality to open edit modal
          },
        }
      ]
    },
    [
      sortModel, 
      carePlanTaskIds, 
      i18n, 
      configurations, 
      handleOpenTask, 
      taskStatuses, 
      handleOnTaskEdit, 
      handleOnTaskCopy, 
      handleAddNoteToTask, 
      handleInternalUrlClick, 
      goalTaskSubTask, 
      isNestedGrid, 
      includeSubTasks,
      buildTaskHandlerMap,
      allTasksEnabled]
  );

  const { startDate, endDate, selectedOwners, status: selectedStatuses } = filters;

  useEffect(() => {
    console.log('service request modal boolean changed');
  }, [isServiceRequestModalOpen]);

  useEffect(() => {
    setTasksGridRows(
      filterTaskGridRows(tasks, { filters: filterValues, searchTextFieldValue }, carePlanTaskIds)
    );
  }, [tasks, filterValues, searchTextFieldValue, carePlanTaskIds]);

  const label = !title && isNestedGrid ? i18n('patients.details.tasks.titleTasks', 'crs') : title;
  const buttonLabel = !title ? i18n('patients.details.tasks.button', 'crs') : buttonTitle;
  const filterLabel = taskBasedOn
    ? i18n('patients.details.tasks.filterPopUp.subTaskTitle', 'crs')
    : i18n('patients.details.tasks.filterPopUp.title', 'crs');

  const taskActivityDefinition = useTaskActivityDefinition();

  const rows = useMemo(() => {
    if (taskBasedOn || basedOn) return taskGridRows;

    let filteredRows = taskGridRows?.filter(
      (x) =>
        (x?.basedOn?.length === 0 || x?.basedOn?.some((e) => !e?.reference?.includes('Task'))) &&
        shouldIncludeTaskByChecklistFilters(x?.wrappedTask, {
          listFilter: filterTasksFromChecklistItem?.listFilter,
          owner: filterTasksFromChecklistItem?.owner,
          status: filterTasksFromChecklistItem?.status,
        })
    );

    // Apply activity definition filtering only when allTasksEnabled is false/undefined
    // and taskActivityDefinitionContext is provided
    // When allTasksEnabled is true, skip activity definition filtering to allow all tasks
    // to be available for status filtering
    if (
      allTasksEnabled !== true &&
      taskActivityDefinitionContext &&
      taskActivityDefinitionContext.length > 0
    ) {
      filteredRows = filteredRows?.filter((x) => {
        const task = x.wrappedTask;
        if (!task?.instantiatesCanonical) return true;
        const activityDefinitionId = getReferenceResourceId(task.instantiatesCanonical);
        return taskActivityDefinitionContext.some((ad: string) => ad === activityDefinitionId);
      });
    }

    return filteredRows;
  }, [
    taskGridRows,
    taskBasedOn,
    basedOn,
    filterTasksFromChecklistItem,
    allTasksEnabled,
    taskActivityDefinitionContext,
    taskActivityDefinitionContext,
  ]);

  return (
    <>
      {pathwaysList && (
        <Box sx={{ ml: 2 }}>
          <Tabs
            scrollButtons={'auto'}
            variant={'scrollable'}
            value={selectedPathway}
            onChange={handlePathwayTabChange}
            aria-label="PathWay Tab"
          >
            {pathwaysList?.map((pathway) => {
              const { code, id } = pathway;
              const getTasks = getTaskGridRows(
                getGoalsByPathWay(code, rows, id),
                taskStatuses?.asList?.() ?? [],
                serviceRequests ?? [],
                configurations?.fhir
              );
              const notSubTask = getTasks?.filter(
                (e) => !e.basedOn?.length || e.basedOn.every((t) => !t?.reference?.includes('Task'))
              );
              const statuses = getStatusCountByPathway(notSubTask, statusCountByPathway);
              return (
                <Tab
                  key={code}
                  label={
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={0.5}>
                      <Typography sx={{ fontSize: '1em', whiteSpace: 'nowrap' }} variant="caption">
                        {code?.toUpperCase()}{' '}
                      </Typography>
                      {Object.entries(statuses).map(([severity, count]) => (
                        <SeverityStatusCount
                          key={severity}
                          count={count}
                          severity={severity as AlertSeverity}
                        />
                      ))}
                    </Box>
                  }
                  value={code}
                />
              );
            })}
          </Tabs>
        </Box>
      )}
      <TaskContextProvider
        menuTaskHandlers={commonMenuTaskHandlers}
        taskActivityDefinitionContext={taskActivityDefinitionContext}
      >
        <CollapsibleDataGrid
          showExpandIcon={includeSubTasks}
          getRowHeight={() => 'auto'}
          addButtonTitle={buttonLabel ?? ''}
          title={label}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          showPagination={showPagination}
          childrenButtons={childrenButtons}
          onAddButtonClick={() => {
            updateState({
              isTaskModalOpen: true,
              taskToEdit: null,
            });
          }}
          onFilterDrawerOpen={onFilterDrawerOpen}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => {
            const { wrappedTask } = params.row as TaskGridRowData;
            handleOpenTask(wrappedTask);
          }}
          renderCollapsibleContent={({ wrappedTask, subTasks: rowSubTasks }: TaskGridRowData) => {
            if (!includeSubTasks) return null;
            const subTasks: WrappedTask[] = (rowSubTasks ?? []).filter((x) =>
              shouldIncludeTaskByChecklistFilters(x, {
                listFilter: filterTasksFromChecklistItem?.subListFilter,
                owner: filterTasksFromChecklistItem?.owner,
                status: filterTasksFromChecklistItem?.status,
              })
            );
            return (
              <TaskContextProvider
                menuTaskHandlers={commonMenuTaskHandlers}
                taskActivityDefinitionContext={taskActivityDefinition?.subtasks}
              >
                <CaseTaskGrid
                  patient={patient}
                  carePlan={carePlan}
                  tasks={subTasks}
                  taskBasedOn={wrappedTask}
                  isNestedGrid={true}
                  showPagination={false}
                  goal={goal}
                  title={i18n('patients.details.tasks.titleSubTasks', 'crs')}
                  buttonTitle={i18n('patients.details.tasks.buttonSubTask', 'crs')}
                  conditions={conditions}
                  observations={observations}
                />
              </TaskContextProvider>
            );
          }}
          subGridRowsExtractor={({ subTasks }: TaskGridRowData) => subTasks ?? []}
          onSearchTextFieldChange={onSearchTextFieldChange}
          searchTextFieldValue={searchTextFieldValue}
          isNestedGrid={isNestedGrid}
          Filters={
            <TaskFilter
              taskStatuses={taskStatuses?.asList?.() ?? []}
              owners={owners}
              startDate={startDate}
              endDate={endDate}
              selectedOwners={selectedOwners}
              selectedStatuses={selectedStatuses}
              onStartDateChange={handleDateFilterChange('startDate')}
              onEndDateChange={handleDateFilterChange('endDate')}
              onOwnerSelectionChange={(_, newValue) => {
                updateFilters({ selectedOwners: newValue });
              }}
              onStatusSelectionChange={(checkedItems) => {
                updateFilters({ status: [...checkedItems] });
              }}
              linked={filters.linked}
              onLinkedChange={(linked) => {
                updateFilters({ linked });
              }}
            />
          }
          FilterDrawerProps={{
            title: filterLabel,
            open: isFilterDrawerOpen,
            onApplyButtonClick: () => {
              onApplyFilters(filters);
            },
            onCloseIconButtonClick: onFilterDrawerClose,
            onClearAllButtonClick: onClearAllFilters,
          }}
        />
      </TaskContextProvider>

      <TaskModalResolver
        goal={goal}
        taskToEdit={taskToEdit as WrappedTask}
        taskBasedOn={taskBasedOn || subTaskBasedOn}
        onSuccessfulEdit={() => dispatchEvent(UPDATE_TASK, { isWorkflowContext })}
        onSuccessfulCreation={() => dispatchEvent(CREATE_TASK, { isWorkflowContext })}
        patientExternal={patient}
        open={isTaskModalOpen}
        carePlan={carePlan}
        onClose={() => {
          updateState({
            taskToEdit: null,
            subTaskBasedOn: null,
            isTaskModalOpen: false,
          });
        }}
        openFrom={openFrom}
      />
      <SearchMemberModal
        open={isSearchMemberModalOpen}
        onCancel={() => {
          updateState({ isSearchMemberModalOpen: false });
        }}
        patient={patient as WrappedPatient}
        onSelectResource={handleOwnerChange}
        memberTypesByConfig={searchMemberOptions?.taskSelectOwner ?? []}
      />
      <Program
        healthCareServices={healthCareServices}
        open={isAssignProgramModalOpen}
        healthCareServiceRelated={taskToEdit?.basedOn as Reference[]}
        onCancel={() => {
          updateState({ isAssignProgramModalOpen: false });
        }}
        handleProgramAssigment={handleTaskToProgram}
      />
      <TaskDetailDrawer
        isAllowedToEdit={!!isAllowedToEdit}
        onEdit={(task) => {
          updateState({ isTaskDetailDrawerOpen: false });
          handleOnTaskEdit(task);
        }}
        open={isTaskDetailDrawerOpen}
        taskExternal={taskToView}
        goal={goal}
        onSuccesfulEdit={() => dispatchEvent(UPDATE_TASK)}
        onCloseIconButtonClick={() => {
          updateState({ isTaskDetailDrawerOpen: false });
        }}
        title={i18n('patients.details.tasks.details', 'crs')}
      />
      <ModalCommunication
        isAllowedToAdd={!!notes?.isAllowedToAdd}
        open={addNoteToTask}
        patient={patient}
        onCancel={() => {
          updateState({ addNoteToTask: false });
        }}
        carePlan={carePlan}
        resource={taskToEdit}
        typeNote="notes_task"
        i18n={i18n}
      />
      <IframeModal
        open={isIframeModalOpen}
        url={urlData?.url}
        title={urlData?.label ?? ''}
        onClose={() => {
          updateState({ isIframeModalOpen: false, urlData: null });
        }}
      />
      <AddReferral
        title={i18n('patients.details.serviceRequests.titleModal', 'crs')}
        openAdd={isServiceRequestModalOpen}
        referral={taskOutputServiceRequest}
        carePlan={carePlan}
        onReferralSuccess={handleUpdateTaskFromResource}
        patient={patient}
        task={taskToEdit}
        onCancel={() => {
          updateState({
            taskToEdit: null,
            isServiceRequestModalOpen: false,
            taskOutputServiceRequest: null,
          });
        }}
        externalValues={{ planDefinition: planDefinition?.[taskType ?? ''] }}
        conditions={conditions || []}
        observations={observations || []}
      />
      <AppointmentForm
        patient={patient}
        appointment={null}
        appointmentId={taskOuputAppointmentId}
        open={isAppointmentModalOpen}
        onClose={() => updateState({ isAppointmentModalOpen: false })}
        handleAppointment={handleUpdateTaskFromResource}
      />
    </>
  );
};

export type OnOwnerSelectionChange = (event: React.SyntheticEvent, newValue: Reference[]) => void;
interface TaskFilterProps {
  owners: Reference[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  selectedOwners: Reference[];
  onStartDateChange: onDateChange;
  onEndDateChange: onDateChange;
  onOwnerSelectionChange: OnOwnerSelectionChange;
  onStatusSelectionChange: (checkedStatus: TaskStatus[]) => void;
  selectedStatuses: TaskStatus[];
  taskStatuses: TaskStatus[];
  linked: boolean;
  onLinkedChange: (linked: boolean) => void;
}

const TaskFilter = ({
  owners,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onOwnerSelectionChange,
  selectedOwners,
  onStatusSelectionChange,
  selectedStatuses,
  taskStatuses,
  linked,
  onLinkedChange,
}: TaskFilterProps) => (
  <Box py={3}>
    <Box marginBottom={1}>
      <PeriodFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
      />
    </Box>

    <CheckboxList
      containerSx={{ marginBottom: 4, marginTop: 3 }}
      title="Status"
      keyExtractor={(item) => item.code as string}
      labelExtractor={(item) => item.display}
      onChange={onStatusSelectionChange}
      options={taskStatuses}
      externalCheckedItems={selectedStatuses}
    />

    <Box sx={{ marginBottom: 4, marginTop: 3 }}>
      <Typography mb={1} fontWeight={'bold'}>
        Linked
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={linked} onChange={(e) => onLinkedChange(e.target.checked)} />}
        label="Show only linked tasks"
      />
    </Box>

    <Box>
      <Typography mb={1} fontWeight={'bold'}>
        Owner
      </Typography>
      <Autocomplete
        value={selectedOwners}
        fullWidth
        multiple
        onChange={onOwnerSelectionChange}
        options={owners}
        getOptionLabel={(option) => option?.display || ''}
        defaultValue={[]}
        renderInput={(params) => <TextField {...params} variant="outlined" />}
      />
    </Box>
  </Box>
);

export default TasksGrid;
